import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPut } from "../../lib";

/**
 * Trade Step 2 — the appraisals, a port of trade_in.php.
 *
 * Row colour follows the legacy rule: an explicit status wins, and where
 * there is none the answer to "interested?" colours the row instead.
 */

const INTERESTED = ["Pending", "Yes", "No"];
const STATUSES = ["", "Completed", "Canceled"];

const TINT = {
  Completed: "27,94,32",
  Canceled: "183,28,28",
  Yes: "106,27,154",
  No: "141,110,79",
  Pending: "13,71,161",
};

const tintStyles = Object.entries(TINT).reduce((acc, [name, rgb]) => {
  acc[`tint${name}`] = {
    backgroundColor: `rgba(${rgb}, 0.18)`,
    "&:hover": { backgroundColor: `rgba(${rgb}, 0.28)` },
  };
  return acc;
}, {});

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1400, margin: "24px auto 0", width: "100%" },
  panel: { padding: theme.spacing(2.5) },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  title: { fontSize: 18, fontWeight: 500, flex: "none" },
  spacer: { flex: 1 },
  search: {
    width: 300,
    "& .MuiOutlinedInput-root": { height: 31 },
    "& .MuiOutlinedInput-input": { padding: "0 4px", fontSize: 13 },
    "& .MuiInputAdornment-root": { marginRight: 2 },
  },
  searchIcon: { fontSize: 17, color: theme.palette.text.secondary },
  count: { color: theme.palette.text.secondary, fontSize: 13 },
  row: { height: 38 },
  cell: { fontSize: 13, padding: "4px 10px" },
  headCell: { fontSize: 12, fontWeight: 600, padding: "6px 10px" },
  strong: { color: "rgba(0,0,0,0.87)" },
  muted: { color: theme.palette.text.secondary },
  truncate: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 0 },
  notesCell: { minWidth: 280 },
  link: {
    color: theme.palette.primary.main,
    cursor: "pointer",
    textDecoration: "underline",
    "&:hover": { textDecoration: "underline", filter: "brightness(0.85)" },
  },
  /* a { color: inherit; text-decoration: none } is set globally, so a mailto
     has to say it is a link. */
  mailto: {
    color: theme.palette.primary.main,
    textDecoration: "underline",
    "&:hover": { filter: "brightness(0.85)" },
  },
  pick: { fontSize: 12.5, "&:before": { borderBottom: "1px solid rgba(0,0,0,0.15)" } },
  noteInput: { fontSize: 12.5, width: "100%", minWidth: 260 },
  saved: { color: "#1b5e20", fontSize: 11, marginLeft: 6 },
  loading: { padding: theme.spacing(4), textAlign: "center" },
  error: { color: "#c62828", fontSize: 13 },
  ...tintStyles,
}));

const when = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleDateString();
};

export function TradeInsPage() {
  const classes = useStyles();
  const history = useHistory();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [interested, setInterested] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (interested) q.set("interested", interested);
    if (query) q.set("search", query);
    const { data, error: err } = await authGet(`/trade-ins?${q.toString()}`);
    setLoading(false);

    if (err) {
      setError(err.message || "Could not load trade-ins.");
      return;
    }
    setError("");
    setRows(data?.rows ?? []);
    setTotal(data?.total ?? 0);
  }, [interested, query]);

  useEffect(() => {
    load();
  }, [load]);

  // A new filter means a new result set; page 3 of the old one is meaningless.
  useEffect(() => {
    setPage(0);
  }, [interested, query]);

  /*
   * Rendered a page at a time, as the legacy grid did at 100 rows.
   * Every row carries a live Select, so drawing all 500 at once puts
   * hundreds of MUI controls on the page and the screen crawls — which
   * reads as a slow search even though the query takes milliseconds.
   */
  const shown = rows.slice(page * perPage, page * perPage + perPage);

  const flash = (id) => {
    setSaved((s) => ({ ...s, [id]: true }));
    setTimeout(() => setSaved((s) => ({ ...s, [id]: false })), 1500);
  };

  const patch = (id, fields) =>
    setRows((list) => list.map((x) => (x.trade_in_id === id ? { ...x, ...fields } : x)));

  const setStatus = (r) => async (e) => {
    const next = e.target.value;
    patch(r.trade_in_id, { status: next || null });
    const { error: err } = await authPut(`/trade-ins/${r.trade_in_id}/status`, { status: next });
    if (err) {
      setError(err.message || "Could not save that status.");
      load();
      return;
    }
    flash(r.trade_in_id);
  };

  const saveNotes = (r) => async () => {
    const { error: err } = await authPut(`/trade-ins/${r.trade_in_id}/notes`, {
      notes: r.notes ?? "",
    });
    if (err) {
      setError(err.message || "Could not save those notes.");
      return;
    }
    flash(r.trade_in_id);
  };

  const submit = (e) => {
    e?.preventDefault?.();
    setQuery(search.trim());
  };

  /** Status wins; otherwise the interested answer carries the colour. */
  const tintFor = (r) => classes[`tint${r.status || r.interested || "Pending"}`] ?? "";

  return (
    <Box className={classes.page}>
      <Paper className={classes.panel}>
        <div className={classes.head}>
          <Typography className={classes.title}>Trade Step 2 — Trade-Ins</Typography>

          <ButtonGroup size="small">
            <Button
              onClick={() => setInterested("")}
              variant={interested === "" ? "contained" : "outlined"}
              color={interested === "" ? "primary" : "default"}
            >
              All
            </Button>
            {INTERESTED.map((s) => (
              <Button
                key={s}
                onClick={() => setInterested(s)}
                variant={interested === s ? "contained" : "outlined"}
                color={interested === s ? "primary" : "default"}
              >
                {s}
              </Button>
            ))}
          </ButtonGroup>

          <form onSubmit={submit}>
            <TextField
              className={classes.search}
              placeholder="Name, email, brand, model or serial"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={submit}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className={classes.searchIcon} />
                  </InputAdornment>
                ),
              }}
            />
          </form>

          <div className={classes.spacer} />
          <span className={classes.count}>
            {loading ? "Loading…" : `${total.toLocaleString()} shown`}
          </span>
        </div>

        {error && <div className={classes.error}>{error}</div>}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.headCell} style={{ width: 80 }}>ID</TableCell>
              <TableCell className={classes.headCell} style={{ width: 95 }}>Date</TableCell>
              <TableCell className={classes.headCell} style={{ width: 150 }}>Name</TableCell>
              <TableCell className={classes.headCell} style={{ width: 230 }}>Email</TableCell>
              <TableCell className={classes.headCell}>Watch</TableCell>
              <TableCell className={classes.headCell} style={{ width: 90 }}>Interested</TableCell>
              <TableCell className={classes.headCell} style={{ width: 130 }}>Status</TableCell>
              <TableCell className={classes.headCell} style={{ width: 300 }}>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((r) => (
              <TableRow key={r.trade_in_id} className={`${classes.row} ${tintFor(r)}`}>
                <TableCell className={`${classes.cell} ${classes.strong}`}>
                  <span
                    className={classes.link}
                    onClick={() => history.push(`/trade-ins/${r.trade_in_id}`)}
                  >
                    {r.trade_in_id}
                  </span>
                </TableCell>
                <TableCell className={classes.cell}>{when(r.created)}</TableCell>
                <TableCell className={`${classes.cell} ${classes.strong}`}>
                  {[r.first_name, r.last_name].filter(Boolean).join(" ")}
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.truncate}`} title={r.email}>
                  <a className={classes.mailto} href={`mailto:${r.email}`}>
                    {r.email}
                  </a>
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.truncate}`}>
                  {[r.brand, r.model].filter(Boolean).join(" ")}
                </TableCell>
                <TableCell className={classes.cell}>{r.interested || "Pending"}</TableCell>
                <TableCell className={classes.cell}>
                  <Select
                    value={r.status || ""}
                    onChange={setStatus(r)}
                    displayEmpty
                    disableUnderline
                    className={classes.pick}
                    MenuProps={{
                      getContentAnchorEl: null,
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                      MenuListProps: { dense: true, disablePadding: true },
                    }}
                  >
                    {STATUSES.map((s) => (
                      <MenuItem key={s || "none"} value={s}>
                        {s || <em>None</em>}
                      </MenuItem>
                    ))}
                  </Select>
                  {saved[r.trade_in_id] && <span className={classes.saved}>Saved</span>}
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.notesCell}`}>
                  <TextField
                    variant="standard"
                    InputProps={{ className: classes.noteInput }}
                    placeholder="Notes…"
                    value={r.notes ?? ""}
                    onChange={(e) => patch(r.trade_in_id, { notes: e.target.value })}
                    onBlur={saveNotes(r)}
                    onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>


        {!loading && rows.length > perPage && (
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onChangePage={(_, p) => setPage(p)}
            rowsPerPage={perPage}
            onChangeRowsPerPage={(e) => {
              setPerPage(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[50, 100, 250, 500]}
          />
        )}

        {loading && (
          <div className={classes.loading}>
            <CircularProgress size={22} />
          </div>
        )}
        {!loading && !rows.length && (
          <div className={classes.loading}>
            <span className={classes.muted}>No trade-ins match.</span>
          </div>
        )}
      </Paper>
    </Box>
  );
}

export default TradeInsPage;
